import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  DeviceCredentials,
  SipConnection,
} from "@/features/devices/types";
import { useTranslation } from "@/features/utils/i18n";
import { Copy, WarningCircle } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";

const Row = ({ label, value }: { label: string; value: string }) => {
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    toast.success(label);
  };

  return (
    // Wrapped rather than truncated: a SIP address is long enough to overflow
    // the dialog and it is here to be read and retyped into a handset, so
    // hiding its tail behind an ellipsis hides the part that differs.
    <div className="flex items-center justify-between gap-2 rounded-md border px-3 py-2">
      <div className="min-w-0 space-y-0.5">
        <div className="text-muted-foreground text-xs">{label}</div>
        <div className="font-mono text-sm break-all">{value}</div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="shrink-0"
        onClick={copy}
        type="button"
      >
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  );
};

/**
 * The password as it can still be shown once it is gone: its first two
 * characters, kept on the handset's row on purpose (`sip_password_hint`).
 *
 * No copy button, unlike {@link Row}: what is on screen is not a value anybody
 * can paste anywhere, and offering to copy it would suggest otherwise. It
 * answers one question — "is the password on this phone the one I have written
 * down?" — and the honest answer to it is two characters, not a button that
 * puts `ab••••••` on the clipboard.
 *
 * A handset issued before the column existed has no hint and says so: an empty
 * string rendered as `••••••` would read as a password whose first characters
 * happen to be hidden, which is the one thing it is not.
 */
const PasswordHintRow = ({ hint }: { hint?: string }) => {
  const { t } = useTranslation("devices");

  return (
    <div className="space-y-0.5 rounded-md border px-3 py-2">
      <div className="text-muted-foreground text-xs">
        {t("devices.credentials.password")}
      </div>
      {hint ? (
        <div className="font-mono text-sm">
          {hint}
          {/*
            A fixed run of dots rather than the 16 characters actually missing:
            the length of what we issue is the backend's business, and a mask
            that tracks it would quietly become a second, wrong copy of it the
            day it changes.
          */}
          <span className="text-muted-foreground">••••••••</span>
        </div>
      ) : (
        <div className="text-muted-foreground text-sm">
          {t("devices.credentials.hintUnknown")}
        </div>
      )}
      <div className="text-muted-foreground text-xs">
        {t("devices.credentials.hintExplanation")}
      </div>
    </div>
  );
};

/**
 * What stands where the password would be, when there is no password to show.
 *
 * Two steps, in place, rather than a second dialog on top of this one. Issuing
 * new credentials does not "reset" anything — it invalidates what the phone
 * currently holds, so a handset that was working stops until someone walks over
 * and retypes them. That consequence belongs next to the button, not behind it.
 */
const RegenerateRow = ({
  onRegenerate,
  regenerating,
}: {
  onRegenerate: () => void;
  regenerating: boolean;
}) => {
  const { t } = useTranslation("devices");
  const [confirming, setConfirming] = useState(false);

  if (!confirming)
    return (
      <Button
        variant="outline"
        className="w-full"
        type="button"
        onClick={() => setConfirming(true)}
      >
        {t("devices.credentials.regenerate")}
      </Button>
    );

  return (
    <div className="space-y-2 rounded-md border border-destructive/50 px-3 py-2">
      <div className="text-destructive flex items-start gap-2 text-sm">
        <WarningCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{t("devices.credentials.regenerateWarning")}</span>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={() => setConfirming(false)}
          disabled={regenerating}
        >
          {t("devices.credentials.cancel")}
        </Button>
        <Button
          variant="destructive"
          size="sm"
          type="button"
          onClick={onRegenerate}
          disabled={regenerating}
        >
          {t("devices.credentials.regenerateConfirm")}
        </Button>
      </div>
    </div>
  );
};

/**
 * A handset's connection settings, in two states.
 *
 * **Just issued** — `credentials` carries the plaintext password. It does not
 * exist on the server (only its HA1 does), so this is the only place it will
 * ever be readable, hence the warning.
 *
 * **Consulted later** — the same screen without the password, because it was
 * never stored. What stands in its place is its first two characters and the
 * way out: an action to issue new credentials. The hint is there because the
 * question this dialog is usually opened for is not "what do I type into the
 * phone" but "is what the client typed the password we gave them" — and
 * re-issuing to find out takes a working handset offline to answer a question
 * about a handset that works.
 */
export const DeviceCredentialsDialog = ({
  connection,
  password,
  passwordHint,
  onRegenerate,
  regenerating,
  onClose,
}: {
  connection: SipConnection | DeviceCredentials | null;
  /** Present only just after issuance. Absent means "consulting". */
  password?: string;
  /** The stored first two characters. Shown whenever `password` is not. */
  passwordHint?: string;
  /** Omitted when the caller has no way to re-issue (or no permission to). */
  onRegenerate?: () => void;
  regenerating?: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation("devices");
  const credentials = connection;

  return (
    <Dialog open={!!credentials} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("devices.credentials.title")}</DialogTitle>
          <DialogDescription>
            {t("devices.credentials.description")}
          </DialogDescription>
        </DialogHeader>

        {credentials && (
          <div className="space-y-3">
            {password && (
              <div className="text-destructive flex items-start gap-2 text-sm">
                <WarningCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t("devices.credentials.warning")}</span>
              </div>
            )}

            {/*
              Two fields, because two is what a softphone asks for.

              `sip_uri` already carries the username, the domain, the port and —
              when it is not the default — the transport, so everything below the
              fold is a second spelling of what is in this one line. Showing all
              seven side by side made the address look like one option among
              many, and left the client deciding which fields matter. They do not
              have the information to decide that; we do.
            */}
            <Row
              label={t("devices.credentials.sipUri")}
              value={credentials.sip_uri}
            />
            {password ? (
              <Row label={t("devices.credentials.password")} value={password} />
            ) : (
              <>
                <PasswordHintRow hint={passwordHint} />
                {onRegenerate && (
                  <RegenerateRow
                    onRegenerate={onRegenerate}
                    regenerating={!!regenerating}
                  />
                )}
              </>
            )}

            {/*
              Folded rather than dropped. A handset that wants the parts
              separately is a minority, but it is a minority that would
              otherwise be stuck — and support reads the realm out loud often
              enough that removing it would only move the question here.
            */}
            <Accordion type="single" collapsible>
              <AccordionItem value="advanced" className="border-none">
                <AccordionTrigger className="py-2 text-sm font-normal">
                  {t("devices.credentials.advanced")}
                </AccordionTrigger>
                <AccordionContent className="pb-0">
                  <div className="space-y-3 pt-1">
                    <Row
                      label={t("devices.credentials.username")}
                      value={credentials.sip_username}
                    />
                    <Row
                      label={t("devices.credentials.domain")}
                      value={credentials.domain}
                    />
                    <Row
                      label={t("devices.credentials.port")}
                      value={String(credentials.port)}
                    />
                    {/*
                      Next to the port, because the two are read together and a
                      phone set to the right port with the wrong transport fails
                      exactly like one nothing is listening for: a timeout, and
                      no server-side trace.
                    */}
                    <Row
                      label={t("devices.credentials.transport")}
                      // Defaulted rather than asserted: this dialog is the only
                      // time the password is ever readable, so a field missing
                      // from an older API must not be allowed to throw the whole
                      // thing away.
                      value={(credentials.transport ?? "udp").toUpperCase()}
                    />
                    <Row
                      label={t("devices.credentials.realm")}
                      value={credentials.sip_realm}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        )}

        <DialogFooter>
          <Button onClick={onClose}>
            {t(
              password
                ? "devices.credentials.done"
                : "devices.credentials.close",
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
